import bilAPI from "./api";

// Timetable GENERATION runs — the solver loop: start a run, poll it while it is
// in flight, edit the draft board, then commit it over the school's live grid.
//
// Two things to know before calling anything here:
//
//  * A run is asynchronous. POST returns 201 with status "queued"; the board
//    only fills in once the worker finishes. Poll `getRun` until `is_reviewable`
//    is true or the status is terminal (succeeded | partial | infeasible |
//    failed | cancelled | committed).
//  * `commitRun` is DESTRUCTIVE: it replaces the school's entire active grid,
//    including hand-entered and CSV-imported rows. Always show
//    `commitPreview` (especially `hand_authored_at_risk`) and get an explicit
//    confirmation first.
//
// Errors from this surface are machine-readable: a 400 carries
// `{ code, detail, details }` (e.g. code "already_running", "slot_taken").
// Branch on `err.response.data.code` and fall back to `detail` — do NOT pipe
// these bodies through `formatApiError`, which would render "code: slot_taken".

// Runs visible to the caller (own school + subtree), newest first.
// `organization` is a school uuid; it maps to the backend's `organization__uuid`
// filter. Paginated envelope: { count, next, previous, results }.
export const listRuns = ({
  params: { status, organization, page, page_size } = {},
  signal,
} = {}) => {
  return new Promise((resolve, reject) => {
    try {
      bilAPI
        .get(`/org/timetable-runs/`, {
          params: { status, organization__uuid: organization, page, page_size },
          signal,
        })
        .then((res) => resolve(res.data))
        .catch((err) => reject(err));
    } catch (error) {
      reject(error);
    }
  });
};

// Start a run for the CALLER's own school (never client-supplied).
// body: { term?, carry_locks?: "run"|"grid"|"none", from_run? }
//   carry_locks "run"  — carry the locked cards of a previous run (the normal loop)
//   carry_locks "grid" — carry the committed timetable (a school with no prior run)
//   carry_locks "none" — start clean
// Resolves the run stub ({ uuid, status: "queued", ... }); rejects 400
// { code: "already_running" } when this school already has a run in flight.
export const startRun = ({ body, signal } = {}) => {
  return new Promise((resolve, reject) => {
    try {
      bilAPI
        .post(`/org/timetable-runs/`, body, { signal })
        .then((res) => resolve(res.data))
        .catch((err) => reject(err));
    } catch (error) {
      reject(error);
    }
  });
};

// The run AND its full draft board. This is the poll target while in flight and
// the refetch target after an edit. Placements with weekday === null &&
// period === null ARE the unplaced tray — there is no separate list.
export const getRun = ({ uuid, signal } = {}) => {
  return new Promise((resolve, reject) => {
    try {
      bilAPI
        .get(`/org/timetable-runs/${uuid}/`, { signal })
        .then((res) => resolve(res.data))
        .catch((err) => reject(err));
    } catch (error) {
      reject(error);
    }
  });
};

// Move one card. `weekday` is JS getDay() order (0 = Sunday … 6 = Saturday) and
// `period` is 1-based. Passing BOTH as null sends the card back to the tray.
// Rejects 400 { code: "slot_taken", details: [...] } on a collision.
export const movePlacement = ({
  uuid,
  placementUuid,
  body,
  signal,
} = {}) => {
  return new Promise((resolve, reject) => {
    try {
      bilAPI
        .patch(
          `/org/timetable-runs/${uuid}/placements/${placementUuid}/move/`,
          body,
          { signal },
        )
        .then((res) => resolve(res.data))
        .catch((err) => reject(err));
    } catch (error) {
      reject(error);
    }
  });
};

// Pin / unpin a card. Locked cards are held fixed by the next regeneration
// started with carry_locks: "run".
export const lockPlacement = ({
  uuid,
  placementUuid,
  is_locked,
  signal,
} = {}) => {
  return new Promise((resolve, reject) => {
    try {
      bilAPI
        .patch(
          `/org/timetable-runs/${uuid}/placements/${placementUuid}/lock/`,
          { is_locked },
          { signal },
        )
        .then((res) => resolve(res.data))
        .catch((err) => reject(err));
    } catch (error) {
      reject(error);
    }
  });
};

// What committing this board WOULD destroy — read-only, writes nothing.
// { will_remove, will_remove_by_source, hand_authored_at_risk, will_write_cards,
//   cards_in_tray, reviewable }
export const commitPreview = ({ uuid, signal } = {}) => {
  return new Promise((resolve, reject) => {
    try {
      bilAPI
        .get(`/org/timetable-runs/${uuid}/commit-preview/`, { signal })
        .then((res) => resolve(res.data))
        .catch((err) => reject(err));
    } catch (error) {
      reject(error);
    }
  });
};

// DESTRUCTIVE. Replaces the school's whole active timetable with this board.
// Resolves { run, written, removed, skipped: [...] }.
export const commitRun = ({ uuid, signal } = {}) => {
  return new Promise((resolve, reject) => {
    try {
      bilAPI
        .post(`/org/timetable-runs/${uuid}/commit/`, null, { signal })
        .then((res) => resolve(res.data))
        .catch((err) => reject(err));
    } catch (error) {
      reject(error);
    }
  });
};

// Stop a queued/running solve and DISCARD its board. Rejects 400
// { code: "not_in_flight" } when the run has already finished.
// See `stopRun` below for the non-destructive sibling — the two are one word
// apart and mean opposite things to a manager.
export const cancelRun = ({ uuid, signal } = {}) => {
  return new Promise((resolve, reject) => {
    try {
      bilAPI
        .post(`/org/timetable-runs/${uuid}/cancel/`, null, { signal })
        .then((res) => resolve(res.data))
        .catch((err) => reject(err));
    } catch (error) {
      reject(error);
    }
  });
};

// Stop searching but KEEP the board found so far. The run stays in flight until
// the worker notices (within ~2s), then finishes normally as succeeded/partial
// with `stop_reason: "user_stop"` — so keep polling after this resolves.
//
// This replaced the old "time to spend (seconds)" input. Instead of asking up
// front for a number of seconds nobody can predict, let the manager look at a
// board and decide it is good enough. Rejects 400 { code: "not_in_flight" }.
export const stopRun = ({ uuid, signal } = {}) => {
  return new Promise((resolve, reject) => {
    try {
      bilAPI
        .post(`/org/timetable-runs/${uuid}/stop/`, null, { signal })
        .then((res) => resolve(res.data))
        .catch((err) => reject(err));
    } catch (error) {
      reject(error);
    }
  });
};

// What a run WOULD involve, without starting one — always the caller's own
// school. { cards, periods_total, classes, teachers, slots,
// room_constrained_cards, soft_rules, hard_rules, budget_seconds,
// tightest_class_fill, findings: [{ code, message }], warnings: [] }
//
// `findings` is the pre-flight check, and it is pure arithmetic ("this class
// needs 44 periods but the week holds 40") — so it is shown BEFORE the manager
// waits for a solve rather than after a failed one.
export const getRunEstimate = ({ params: { term } = {}, signal } = {}) => {
  return new Promise((resolve, reject) => {
    try {
      bilAPI
        .get(`/org/timetable-runs/estimate/`, { params: { term }, signal })
        .then((res) => resolve(res.data))
        .catch((err) => reject(err));
    } catch (error) {
      reject(error);
    }
  });
};
